'use client'

import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import '@/styles/bauhaus.css'
import { useLanguage } from '@/i18n/LanguageProvider'
import { appLanguageToContentLocale } from '@/i18n/contentLocale'
import chinaPoetryData from '../china-poetry.json'

type Language = 'zh' | 'de' | 'en'

export default function ActivityDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const { t, language } = useLanguage()
  const lang = appLanguageToContentLocale(language) as Language

  // 获取当前活动的数据
  const getActivityData = () => {
    switch (id) {
      case 'collective':
        return chinaPoetryData.sections.collective
      case 'rock-music':
        return chinaPoetryData.sections.rock_music
      case 'moon-tides':
        return chinaPoetryData.sections.moon_tides
      default:
        return null
    }
  }

  const activityData = getActivityData()

  // 诗人列表，包含中英文名字
  const poets = [
    { id: '史三', name: { cn: '史三', en: 'Shi San' } },
    { id: '吴与', name: { cn: '吴与', en: 'Wu Yu' } },
    { id: '周幼安', name: { cn: '周幼安', en: 'Zhou Youan' } },
    { id: '喻折', name: { cn: '喻折', en: 'Yu Zhe' } },
    { id: '宋婷月', name: { cn: '宋婷月', en: 'Song Tingyue' } },
    { id: '左拉娜娜', name: { cn: '左拉娜娜', en: 'Zuo La Nana' } },
    { id: '森', name: { cn: '森', en: 'Sen' } },
    { id: '淼淼', name: { cn: '淼淼', en: 'Miao Miao' } },
    { id: '牧歌', name: { cn: '牧歌', en: 'Muge' } },
    { id: '王樘', name: { cn: '王樘', en: 'Wang Tang' } },
    { id: '高檩', name: { cn: '高檩', en: 'Gao Lin' } },
    { id: '陆闵', name: { cn: '陆闵', en: 'Lu Min' } }
  ]

  if (!activityData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525] flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bauhaus mb-4">404 - Activity Not Found</h1>
          <Link 
            to="/Qiaodong/BPF/china-poetry"
            className="text-[#B9AE9A] hover:text-[#B9AE9A]/80 transition-colors duration-300"
          >
            Return to Activities
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C1C1C] to-[#252525]">
      {/* 页面头部 */}
      <div className="pt-32 pb-16 relative bg-gradient-to-b from-[#1C1C1C] to-[#1C1C1C]">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(120,120,120,0.1)_0%,rgba(0,0,0,0)_70%)]"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* 返回链接 */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <Link 
              to="/Qiaodong/BPF/china-poetry"
              className="inline-flex items-center text-[#B9AE9A]/60 hover:text-[#B9AE9A] transition-colors duration-300 text-sm font-bauhaus"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('bpf.back_to_activities')}
            </Link>
          </motion.div>

          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bauhaus text-white tracking-tight mb-6"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#B9AE9A] to-[#8C4233]">
                {activityData.title[lang]}
              </span>
            </motion.h1>
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {id === 'collective' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {poets.map((poet) => (
              <Link
                key={poet.id}
                to={`/Qiaodong/BPF/china-poetry/${id}/${encodeURIComponent(poet.id)}`}
                className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl p-6 hover:border-[#B9AE9A]/40 transition-colors duration-300"
              >
                <h2 className="text-xl font-bauhaus text-[#B9AE9A] text-center">
                  {lang === 'zh' ? poet.name.cn : poet.name.en}
                </h2>
              </Link>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-[#1C1C1C]/40 backdrop-blur-sm border border-[#B9AE9A]/20 rounded-xl p-8"
          >
            <div className="prose prose-invert max-w-none">
              <div className="text-lg text-white/80 leading-relaxed space-y-6">
                <p className="first-letter:text-3xl first-letter:font-bauhaus first-letter:text-[#B9AE9A]">
                  {activityData.description[lang]}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
} 